import { fmt, fmtKc } from '../../lib/formatters';
import { OK, BAD, ACCENT } from '../../lib/uiTokens';
import KpiKarty from './KpiKarty';
import KDoreseni from './KDoreseni';
import StrukturaPruh from './StrukturaPruh';
import PacingBar from './PacingBar';

/**
 * Pravý sloupec Výsledovky. Norma (`standard-rozvrzeni.md` §1f) mu dává dvě
 * úlohy a žádnou třetí: **statistiky** a **„K dořešení"**. Nic se tu needituje.
 *
 * Stavba odshora: hero s EBITDOU a pruhem k plánu → řádky `popis · hodnota`
 * → tři KPI proti cílům → „kam jde koruna tržeb" → „K dořešení".
 *
 * Vodopád odsud odešel ve 3.480.0 Říkal totéž, co tabulka vlevo (tržby → marže
 * → EBITDA), jen na 240 px svisle; podíl na tržbách, který z tabulky přečíst
 * nejde, vezme `StrukturaPruh` na 60 px.
 */
export default function VysledovkaStatistikyPanel({ data, rezim, cardStyle, isDark }) {
    const s = data?.souhrn;
    if (!s) return null;

    const jeProj = rezim === 'projekce';
    const tilde = jeProj ? '~' : '';

    const ebitda    = jeProj ? s.ebitda_projekce : s.ebitda;
    const ebitdaPct = jeProj ? s.ebitda_projekce_pct : s.ebitda_pct;
    const trzby     = jeProj ? s.ciste_trzby_projekce : s.ciste_trzby;
    const marze     = jeProj ? s.hruba_marze_projekce : s.hruba_marze;

    const v = data.verdikt?.[jeProj ? 'projekce' : 'zauctovano'];
    const tempo = s.ebitda_plan ? ebitda / s.ebitda_plan : null;

    const Nadpis = ({ children }) => (
        <div className="text-[9.5px] font-bold uppercase mb-2"
            style={{ letterSpacing: '0.12em', color: 'var(--text-secondary)' }}>{children}</div>
    );
    const Radek = ({ popis, hodnota, barva, titulek, posledni }) => (
        <div title={titulek} className="flex items-baseline justify-between gap-2 py-[7px]"
            style={{ borderBottom: posledni ? 'none' : '1px solid var(--border-default)' }}>
            <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{popis}</span>
            <span className="text-[13.5px] font-semibold tabular-nums" style={{ color: barva || 'var(--text-primary)' }}>{hodnota}</span>
        </div>
    );

    const pctText = (x) => (x == null ? '—' : `${String(x).replace('.', ',')} %`);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            {/* Hero: EBITDA a jak stojí proti plánu */}
            <div className="rounded-2xl p-4 flex-shrink-0" style={cardStyle}>
                <Nadpis>EBITDA · {jeProj ? 'projekce měsíce' : 'zaúčtováno'}</Nadpis>
                <div className="flex items-baseline gap-2.5">
                    <div className="text-[30px] font-extrabold tabular-nums leading-none">{tilde}{fmtKc(ebitda)}</div>
                    {ebitdaPct != null && (
                        <div className="text-[14px] font-bold" style={{ color: 'var(--text-secondary)' }}>{pctText(ebitdaPct)}</div>
                    )}
                </div>
                {s.ebitda_plan != null && (
                    <div className="mt-3">
                        <PacingBar tempo={tempo} podil={jeProj ? 1 : (data.pacing?.podil ?? 0)}
                            color={ACCENT} isDark={isDark} jeVyhoda />
                        <div className="flex items-center justify-between text-[11px] mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                            <span>{jeProj ? 'plán měsíce' : 'plán k dnešku'} {fmtKc(jeProj ? s.ebitda_plan : v?.ebitda_ref)}</span>
                            {v?.delta != null && (
                                <span className="tabular-nums font-semibold" style={{ color: v.delta >= 0 ? OK : BAD }}>
                                    {v.delta >= 0 ? '+' : '−'}{fmtKc(Math.abs(v.delta))}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Statistiky období — řádky popis · hodnota, jak to má norma */}
            <div className="rounded-2xl px-4 py-3.5 flex-shrink-0" style={cardStyle}>
                <Nadpis>{jeProj ? 'Projekce do konce měsíce' : 'Zaúčtováno k dnešku'}</Nadpis>
                <Radek popis="Čisté tržby"
                    hodnota={<>{tilde}{fmt(trzby)}{v?.trzby_delta != null && (
                        <span className="text-[11.5px] font-medium ml-1" style={{ color: v.trzby_delta >= 0 ? OK : BAD }}>
                            {v.trzby_delta >= 0 ? '+' : '−'}{fmt(Math.abs(v.trzby_delta))}
                        </span>
                    )}</>}
                    titulek={`plán ${fmtKc(v?.trzby_ref)}`} />
                <Radek popis="Hrubá marže"
                    hodnota={<>{tilde}{fmt(marze)}{trzby ? (
                        <span className="text-[11.5px] font-medium ml-1" style={{ color: 'var(--text-secondary)' }}>
                            {String(Math.round(marze / trzby * 1000) / 10).replace('.', ',')} %
                        </span>
                    ) : null}</>} />
                <Radek popis="Náklady celkem" hodnota={<>−{fmt((trzby ?? 0) - (ebitda ?? 0))}</>} posledni />
            </div>

            <KpiKarty kpi={data.kpi} rezim={rezim} cardStyle={cardStyle} />

            <StrukturaPruh segmenty={data.struktura?.[jeProj ? 'projekce' : 'zauctovano']} cardStyle={cardStyle} />

            <div className="flex-1 min-h-0">
                <KDoreseni data={data} rezim={rezim} cardStyle={cardStyle} />
            </div>
        </div>
    );
}
